import { colors } from '@lmc/common/constants'
import _ from 'lodash'
import React from 'react'
import AnimateHeight from 'react-animate-height'
import styled from 'styled-components'
import {
  DetailsColumnCell,
  RoundColumnCell,
  StatusColumnCell
} from './columnCells'

interface RoundsReportTableRowProps {
  children?: React.ReactNode
  time: string
  due: number
  notSigned: number
  signed: number
  notAdministered: number
  PRN: number
  isDailyTotal?: boolean
  isFinalRow?: boolean
}

interface RenderRowProps extends RoundsReportTableRowProps {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  isEmptyRow: boolean
}

const renderRow = (props: RenderRowProps) => {
  const { isOpen, setIsOpen } = props

  return (
    <Row
      onClick={() => {
        setIsOpen(!isOpen)
      }}
    >
      {/* @ts-ignore */}
      <RoundColumnCell {...props} />
      {/* @ts-ignore */}
      <StatusColumnCell {...props} />
      {/* @ts-ignore */}
      <DetailsColumnCell {...props} />
    </Row>
  )
}

// Main render function

/**
@description Renders a rounds report table row.

@param {string} time. The round time.

@param {number} due. The residents whose meds are due (i.e. not signed).
If a round is the most recent round, the residents will be counted as due.
If a round is not the most recent round, the residents will be counted as
not signed.

@param {number} notSigned. The residents whose meds have not been signed.
These will be meds that are due, but are in rounds in the past, excluding
the most recent round.

@param {number} signed. The residents whose meds have been signed for
(either given or not given).

@param {number} notAdministered. The residents who haven't taken all
their meds.

@param {number} PRN. The residents who have had prn meds.

@param {bool} isFinalRow. True if row is last row in table.

@returns {function} RoundsReportTableRow
*/

export default function RoundsReportTableRow(props: RoundsReportTableRowProps) {
  const {
    children,
    due,
    isDailyTotal,
    isFinalRow,
    notAdministered,
    notSigned,
    PRN,
    signed
  } = props

  const [isOpen, setIsOpen] = React.useState(false)

  const residentsTotal = _.sum([due, notAdministered, notSigned, PRN, signed])
  const isEmptyRow = !residentsTotal

  return (
    <RowContainer
      $isDailyTotal={isDailyTotal}
      $isFinalRow={isFinalRow}
      $isEmptyRow={isEmptyRow}
    >
      {renderRow({ ...props, isEmptyRow, isOpen, setIsOpen })}
      <AnimateHeight animateOpacity duration={200} height={isOpen ? 'auto' : 0}>
        {children}
      </AnimateHeight>
    </RowContainer>
  )
}

const Row = styled.div`
  align-items: center;
  cursor: pointer;
  display: flex;
  height: 72px;
  justify-content: space-between;
  line-height: 22px;
  padding: 0px 16px;
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.lightgrey};
  }
`
Row.displayName = 'Row'

const RowContainer = styled.div<{
  $isDailyTotal?: boolean
  $isFinalRow?: boolean
  $isEmptyRow?: boolean
}>`
  border-bottom: ${(props) =>
    props.$isFinalRow
      ? `2px solid ${colors.darkgrey}`
      : `1px solid ${colors.grey}`};
  pointer-events: ${(props) =>
    props.$isDailyTotal || props.$isEmptyRow ? 'none' : 'auto'};
`
